import { currencySchema } from '@btsis/core';
import { z } from 'zod';
import { moneySchema } from './common/money.js';

/**
 * Where an organization sits in the geographic tree, with the names of the
 * three nodes so no screen has to look them up (docs/decisions/0127). The ids
 * are one country and the zone and region read off it by the server.
 */
export const organizationPlacementSchema = z.object({
  countryId: z.uuid(),
  countryName: z.string(),
  zoneId: z.uuid(),
  zoneName: z.string(),
  regionId: z.uuid(),
  regionName: z.string(),
});

export type OrganizationPlacement = z.infer<typeof organizationPlacementSchema>;

/**
 * What an organization's accounts hold in one currency, from the lines on the
 * accounts and their matches — docs/decisions/0127 §4:
 *
 * - `balance`: credits less debits of the lines not removed;
 * - `incoming`: the credits not excluded, the money that can be matched;
 * - `reconciled`: allocated to donations by confirmed matches;
 * - `pending`: allocated by matches waiting for a second person;
 * - `held`: held by something else — a card payout, a refund, a transfer;
 * - `open`: still unallocated;
 * - `transferred`: moved out to institutions' vaults by completed transfers
 *   (docs/decisions/0129) and already off `balance`.
 *
 * `incoming` is `reconciled + pending + held + open`, always; `balance` is net
 * of what left the account and is not a part of that sum.
 */
export const organizationFiguresShape = {
  currency: currencySchema,
  balance: moneySchema,
  incoming: moneySchema,
  reconciled: moneySchema,
  pending: moneySchema,
  held: moneySchema,
  open: moneySchema,
  transferred: moneySchema,
};

export const organizationFiguresSchema = z.object(organizationFiguresShape);

export type OrganizationFigures = z.infer<typeof organizationFiguresSchema>;

/**
 * One institution's share of the matched money, in the line's currency. An
 * institution the reader's scope does not hold is not named: its shares are
 * folded into one row with every institution field null.
 */
export const institutionShareSchema = z.object({
  institutionId: z.uuid().nullable(),
  institutionCode: z.string().nullable(),
  institutionName: z.string().nullable(),
  reconciled: moneySchema,
  pending: moneySchema,
  transferred: moneySchema,
});

export type InstitutionShare = z.infer<typeof institutionShareSchema>;

export const organizationFiguresWithInstitutionsSchema = organizationFiguresSchema.extend({
  byInstitution: z.array(institutionShareSchema),
});

export type OrganizationFiguresWithInstitutions = z.infer<
  typeof organizationFiguresWithInstitutionsSchema
>;

export const accountFiguresSchema = z.object({
  accountId: z.uuid(),
  figures: z.array(organizationFiguresWithInstitutionsSchema),
});

export type AccountFigures = z.infer<typeof accountFiguresSchema>;

export const organizationAccountFiguresSchema = z.object({
  organizationId: z.uuid(),
  accounts: z.array(accountFiguresSchema),
  summary: z.array(organizationFiguresWithInstitutionsSchema),
});

export type OrganizationAccountFigures = z.infer<typeof organizationAccountFiguresSchema>;
